// Image Card Styles
// Styles for Pinterest-style image cards

import styled from "styled-components";
import { theme } from "../theme";

interface ImageCardContainerProps {
  /** Tile height as a multiple of its width. Portrait photos are > 1. */
  $ratio: number;
}

export const ImageCardContainer = styled.div<ImageCardContainerProps>`
  position: relative;
  width: 100%;
  /**
   * Shape, not size.
   *
   * This used to be a pixel height computed as ratio * 300, on the assumption
   * that a column is 300px wide. On a phone a column is nearer 100px, so every
   * tile rendered roughly three times taller than it was wide -- tall skinny
   * slivers, only a couple of which fit down a column, which is why Explore
   * looked like it held three photos when the API had sent sixty.
   *
   * An aspect ratio asks the column how wide it actually is.
   */
  aspect-ratio: 1 / ${(props) => props.$ratio};
  margin-bottom: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  background: ${theme.colors.background.secondary};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  break-inside: avoid;
  page-break-inside: avoid;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-bottom: ${theme.spacing.sm};
    border-radius: ${theme.borderRadius.md};
  }
`;

export const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  cursor: pointer;
`;

interface ImageProps {
  $loaded: boolean;
}

export const Image = styled.img<ImageProps>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  opacity: ${(props) => (props.$loaded ? 1 : 0)};
  transition: opacity 0.3s ease;
  background: ${theme.colors.background.secondary};
`;

export const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.3) 0%,
    transparent 20%,
    transparent 80%,
    rgba(0, 0, 0, 0.4) 100%
  );
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;

  ${ImageCardContainer}:hover & {
    opacity: 1;
  }
`;

interface ProfileIconProps {
  $avatarUrl: string;
}

export const ProfileIcon = styled.button<ProfileIconProps>`
  position: absolute;
  top: ${theme.spacing.sm};
  left: ${theme.spacing.sm};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid ${theme.colors.background.primary};
  background-image: url(${(props) => props.$avatarUrl});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  pointer-events: auto;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }

  &:active {
    transform: scale(0.95);
  }

  /* Fallback if avatar image fails to load */
  &::after {
    content: "👤";
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background: ${theme.colors.background.secondary};
    border-radius: 50%;
    font-size: 1.25rem;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 36px;
    height: 36px;
    top: ${theme.spacing.xs};
    left: ${theme.spacing.xs};
  }
`;

export const ImageInfo = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: ${theme.spacing.sm};
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;

  ${ImageCardContainer}:hover & {
    opacity: 1;
  }
`;

export const ImageLikes = styled.div`
  position: absolute;
  top: ${theme.spacing.sm};
  right: ${theme.spacing.sm};
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  color: ${theme.colors.text.primary};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-size: 0.875rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: auto;
  z-index: 10;

  ${ImageCardContainer}:hover & {
    opacity: 1;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.8rem;
    padding: ${theme.spacing.xs};
    top: ${theme.spacing.xs};
    right: ${theme.spacing.xs};
  }
`;

export const ImageDescription = styled.p`
  margin: 0;
  color: ${theme.colors.text.primary};
  font-size: 0.875rem;
  line-height: 1.4;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.8rem;
  }
`;

export const ImageErrorFallback = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${theme.colors.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.text.secondary};
  font-size: 0.875rem;
`;

